import {useEffect, useState} from "react"
import {Routes, Route, useParams} from "react-router-dom"

import {FrequencySelection} from "./FrequencySelection"
import {WeeklyPayDay} from "./DateSelection/WeeklyPayDay"
import {FortnightlyPayDay} from "./DateSelection/FortnightlyPayDay"
import {FortnightlyNextPayDate} from "./DateSelection/FortnightlyNextPayDate"
import {MonthlyPayDay} from "./DateSelection/MonthlyPayDay"
import {CalendarCustomStartDate} from "./DateSelection/CustomStartDate"
import {AmountSelectionPage} from "./AmountSelection"
import {PaymentMethodSelection} from "./PaymentMethodSelection"
import {PaymentForm} from "./PaymentForm"

import {ErrorPage} from "@indebted/components/Error"
import {paymentPlanAPI} from "@indebted/api/paymentPlanAPI"
import {LoadingPage} from "@indebted/components/Loading"

type FrequencyOption = {
	ID: string
	DisplayText: string
	Options?: Array<{
		ID: string
		Display: string
	}>
	CustomStartDate?: {
		Display: string
	}
}

type I18nType = {
	FrequencySelection: {
		Header: string
		Title: string
		Question: string
	}
	DateSelection: {
		DateSelectionHeader: string
		PayDateTitle: string
		CustomStartDateHeader: string
		CustomStartDateDescription: string
	}
	PayToday: {
		Header: string
		TitleMonthly: string
		ButtonLabel: string
		CustomStartDateButtonLabel: string
	}
	PaymentMethodSelection: {
		Header: string
		Title: string
		PaymentMethods: Array<{
			ID: string
			DisplayText: string
		}>
	}
}

type PaymentMethodOption = {
	ID: string
	DisplayText: string
}

type PaymentPlanFormProps = {
	options: {
		FrequencyOptions: FrequencyOption[]
		I18n: I18nType
		PaymentMethodOptions: PaymentMethodOption[]
		TodayStartDateEnabled: boolean
		Locale: string
	}
}

function PaymentPlanForm({options}: PaymentPlanFormProps) {
	const frequencyByID = (ID: string): FrequencyOption | undefined => options.FrequencyOptions.find((f) => f.ID === ID)

	return (
		<>
			<Routes>
				<Route
					element={
						<FrequencySelection
							i18n={options.I18n.FrequencySelection}
							frequencies={options.FrequencyOptions}
						/>
					}
					path="/"
				/>
				<Route
					element={
						<WeeklyPayDay
							frequency={frequencyByID("Weekly")}
							i18n={{DateSelection: options.I18n.DateSelection, PayToday: options.I18n.PayToday}}
							todayStartDateEnabled={options.TodayStartDateEnabled}
						/>
					}
					path="/Weekly"
				/>
				<Route
					element={
						<FortnightlyPayDay
							frequency={frequencyByID("Fortnightly")}
							i18n={{DateSelection: options.I18n.DateSelection, PayToday: options.I18n.PayToday}}
							todayStartDateEnabled={options.TodayStartDateEnabled}
						/>
					}
					path="/Fortnightly"
				/>
				<Route
					element={
						<FortnightlyNextPayDate
							frequency={frequencyByID("Fortnightly")}
							i18n={options.I18n.DateSelection}
						/>
					}
					path="/FortnightlyNextPayDate"
				/>
				<Route
					element={
						<MonthlyPayDay
							frequency={frequencyByID("Monthly")}
							i18n={{DateSelection: options.I18n.DateSelection, PayToday: options.I18n.PayToday}}
							todayStartDateEnabled={options.TodayStartDateEnabled}
						/>
					}
					path="/Monthly"
				/>
				<Route
					element={
						<CalendarCustomStartDate
							i18n={options.I18n.DateSelection}
							locale={options.Locale}
							frequencies={options.FrequencyOptions}
						/>
					}
					path="/CustomStartDate"
				/>
				<Route element={<AmountSelectionPage frequencyOptions={options.FrequencyOptions} />} path="/Amount" />
				<Route
					element={
						<PaymentMethodSelection
							i18n={options.I18n.PaymentMethodSelection}
							paymentMethods={options.PaymentMethodOptions}
						/>
					}
					path="/PaymentMethodSelection"
				/>
				<Route element={<PaymentForm />} path="/:paymentMethod" />
			</Routes>
		</>
	)
}

type PaymentPlanPageParams = Record<string, string | undefined> & {
	secureCode: string
}

function PaymentPlanPage() {
	const {secureCode} = useParams<PaymentPlanPageParams>()
	const [Component, setComponent] = useState<ReactNode>(<LoadingPage />)

	useEffect(() => {
		paymentPlanAPI
			.createForm({secureCode})
			.then((result) => {
				setComponent(<PaymentPlanForm options={result} />)
			})
			.catch((error) => {
				setComponent(<ErrorPage message={error.Message} error={error} />)
			})
	}, [secureCode])

	return Component
}

export {PaymentPlanPage}
